/**
 * Portal Login Screen Component
 * Unified username+password login for admin, moderator, and student modes.
 * Guardians do NOT use this screen — they are passwordless and sign in via
 * emailed magic links (guardian-link-login-screen) or the family picker PIN.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/authentication/auth-service';
import { useAuthStore } from '../../services/authentication/auth-store';
import { TurnstileWidget } from '../../components/turnstile-widget';
import { isCaptchaEnabled } from '../../components/captcha';
import type { TurnstileHandle } from '../../components/turnstile-widget';
import { Button } from '../../components/button';
import { FormField, Input, FormMessage } from '../../components/form-field';
import { LanguageSelect } from '../../components/language-select';
import { VersionFooter } from './version-footer';
import type { StaffRole } from '../../api/portal-user';
import auth from './auth.module.css';

interface PortalLoginScreenProps {
  mode: StaffRole;
}

/** Each staff portal's sign-in card: its title, and the line under it. Keys written whole. */
const ROLE_KEY: Record<StaffRole, { title: string; subtitle: string }> = {
  admin: { title: 'login.title.admin', subtitle: 'login.subtitle.admin' },
  moderator: { title: 'login.title.moderator', subtitle: 'login.subtitle.moderator' },
};

export function PortalLoginScreen({ mode }: PortalLoginScreenProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const notice = useAuthStore((state) => state.notice);

  // Captcha (only enforced when a Turnstile sitekey is configured)
  const captchaEnabled = isCaptchaEnabled();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileHandle>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await authService.login({ username, password, mode, captchaToken: captchaToken ?? undefined });
    // Turnstile tokens are single-use — refresh for a possible retry
    captchaRef.current?.reset();
  };

  const { title: titleKey, subtitle: subtitleKey } = ROLE_KEY[mode];

  return (
    <div className={`${auth.loginContainer} portal-shell`}>

      <div className={auth.loginContent}>
        <div className={`${auth.loginCard} framed`}>
          <div className={auth.loginHeader}>
            <h1 className={auth.loginTitle}>{t(titleKey)}</h1>
            <p className={auth.loginSubtitle}>{t(subtitleKey)}</p>
          </div>

          <div className={auth.loginLanguageSelector}>
            <LanguageSelect compact />
          </div>

          <form onSubmit={handleSubmit} className={auth.loginForm}>
            <FormField label={t('login.username')} required>
              {(field) => (
                <Input
                  {...field}
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('login.enterUsername')}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              )}
            </FormField>

            <FormField label={t('login.password')} required>
              {(field) => (
                <Input
                  {...field}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.enterPassword')}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              )}
            </FormField>

            <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />

            {notice === 'session_expired' && !error && (
              <FormMessage tone="info">
                {t('login.sessionExpired')}
              </FormMessage>
            )}

            {error && (
              <FormMessage>
                {error === 'network_error' ? t('login.networkError') : t('login.loginError')}
              </FormMessage>
            )}

            <Button
              type="submit"
              block
              size="lg"
              disabled={!username || !password || isLoading || (captchaEnabled && !captchaToken)}
            >
              {isLoading ? t('common.loading') : t('login.loginButton')}
            </Button>
          </form>
        </div>

        <div className={auth.loginVersionBox}>
          <VersionFooter />
        </div>
      </div>
    </div>
  );
}
